import { useState } from "react"
import { Badge } from "../ui/badge"
import { Button } from "../ui/button"
import Card, { CardContent, CardHeader, CardTitle } from "../Card"
import { Input } from "../ui/input"
import { Label } from "../ui/label"
import { Textarea } from "../ui/textarea"
import { Plus, Trash2, Edit, X, Check } from "lucide-react"

export default function WebinarTranslationForm({ defaultLanguage, translations = [], onChange }) {
  const [editingLanguage, setEditingLanguage] = useState(null)
  const [newTranslation, setNewTranslation] = useState({
    language: "en",
    title: "",
    description: ""
  })
  const [showAddForm, setShowAddForm] = useState(false)

  const handleAddTranslation = (e) => {
    e.preventDefault()
    
    // Vérifier que la langue n'est pas la langue par défaut
    if (newTranslation.language === defaultLanguage) {
      alert(`Impossible d'ajouter une traduction dans la langue par défaut (${defaultLanguage.toUpperCase()})`)
      return
    }

    // Vérifier que la langue n'existe pas déjà
    if (translations.some(t => t.language === newTranslation.language)) {
      alert(`Une traduction en ${newTranslation.language.toUpperCase()} existe déjà`)
      return
    }

    // Vérifier que le titre et la description sont remplis
    if (!newTranslation.title || !newTranslation.description) {
      alert("Le titre et la description sont obligatoires")
      return
    }

    // Ajouter la traduction
    const updatedTranslations = [...translations, { ...newTranslation }]
    onChange(updatedTranslations)
    
    // Réinitialiser le formulaire
    setNewTranslation({ language: "en", title: "", description: "" })
    setShowAddForm(false)
  }

  const handleUpdateTranslation = (language, translationData) => {
    const updatedTranslations = translations.map(t => 
      t.language === language ? { ...t, ...translationData } : t
    )
    onChange(updatedTranslations)
    setEditingLanguage(null)
  }

  const handleDeleteTranslation = (language) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer la traduction en ${language.toUpperCase()} ?`)) {
      return
    }

    const updatedTranslations = translations.filter(t => t.language !== language)
    onChange(updatedTranslations)
  }

  const availableLanguages = ["fr", "en"].filter(
    lang => lang !== defaultLanguage && !translations.some(t => t.language === lang)
  )

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-center">
          <CardTitle>Traductions</CardTitle>
          {!showAddForm && availableLanguages.length > 0 && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setShowAddForm(true)}
            >
              <Plus className="w-4 h-4 mr-2" />
              Ajouter une traduction
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Formulaire d'ajout */}
        {showAddForm && (
          <div className="border rounded-lg p-4 bg-gray-50">
            <form onSubmit={handleAddTranslation} className="space-y-4">
              <div>
                <Label htmlFor="new-language">Langue</Label>
                <select
                  id="new-language"
                  value={newTranslation.language}
                  onChange={(e) => setNewTranslation({ ...newTranslation, language: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#203a8a]"
                  required
                >
                  {availableLanguages.map(lang => (
                    <option key={lang} value={lang}>
                      {lang === "fr" ? "Français" : "Anglais"}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <Label htmlFor="new-title">Titre traduit *</Label>
                <Input
                  id="new-title"
                  value={newTranslation.title}
                  onChange={(e) => setNewTranslation({ ...newTranslation, title: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="new-description">Description traduite *</Label>
                <Textarea
                  id="new-description"
                  value={newTranslation.description}
                  onChange={(e) => setNewTranslation({ ...newTranslation, description: e.target.value })}
                  rows={4}
                  required
                />
              </div>
              <div className="flex gap-2">
                <Button type="submit" size="sm">
                  <Check className="w-4 h-4 mr-2" />
                  Ajouter
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setShowAddForm(false)
                    setNewTranslation({ language: "en", title: "", description: "" })
                  }}
                >
                  <X className="w-4 h-4 mr-2" />
                  Annuler
                </Button>
              </div>
            </form>
          </div>
        )}

        {/* Liste des traductions */}
        {translations.length === 0 ? (
          <p className="text-gray-500 text-sm">
            Aucune traduction disponible. {availableLanguages.length > 0 && "Ajoutez-en une pour commencer."}
          </p>
        ) : (
          <div className="space-y-4">
            {translations.map((translation) => (
              <div key={translation.language} className="border rounded-lg p-4">
                {editingLanguage === translation.language ? (
                  <TranslationEditForm
                    translation={translation}
                    onSave={(data) => handleUpdateTranslation(translation.language, data)}
                    onCancel={() => setEditingLanguage(null)}
                  />
                ) : (
                  <div>
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <Badge className="bg-blue-500 text-white">
                          {translation.language.toUpperCase()}
                        </Badge>
                        <h4 className="font-medium mt-2">{translation.title}</h4>
                        <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                          {translation.description}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          type="button"
                          onClick={() => setEditingLanguage(translation.language)}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          type="button"
                          onClick={() => handleDeleteTranslation(translation.language)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

function TranslationEditForm({ translation, onSave, onCancel }) {
  const [formData, setFormData] = useState({
    title: translation.title,
    description: translation.description
  })

  const handleSubmit = (e) => {
    e.preventDefault()
    onSave(formData)
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <Label htmlFor="edit-title">Titre *</Label>
        <Input
          id="edit-title"
          value={formData.title}
          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
          required
        />
      </div>
      <div>
        <Label htmlFor="edit-description">Description *</Label>
        <Textarea
          id="edit-description"
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          rows={4}
          required
        />
      </div>
      <div className="flex gap-2">
        <Button type="submit" size="sm">
          <Check className="w-4 h-4 mr-2" />
          Enregistrer
        </Button>
        <Button type="button" variant="outline" size="sm" onClick={onCancel}>
          <X className="w-4 h-4 mr-2" />
          Annuler
        </Button>
      </div>
    </form>
  )
}

